import {Observable, throwError} from 'rxjs';
import {of} from 'rxjs';
import {LogEntry} from '@app/service/logger/log-entry';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {ServerResponse} from '@shared/interfaces';
import {environment} from '@env';
import {v4 as uuidv4} from 'uuid';

// ****************************************************
// Log Publisher Abstract Class
// NOTE: This class must be located BEFORE
//       all those that extend this class
// ****************************************************
export abstract class LogPublisher {
  location: string;

  abstract log(record: LogEntry): Observable<boolean> | Promise<any>;

  abstract clear(): Observable<boolean>;
}

// ****************************************************
// Console Logging Class
// ****************************************************
export class LogConsole extends LogPublisher {
  log(entry: LogEntry): Observable<boolean> {
    // Log to console
    console.log(entry.buildLogString());
    return of(true);
  }

  clear(): Observable<boolean> {
    console.clear();
    return of(true);
  }
}


// ****************************************************
// Local Storage Logging Class
// ****************************************************
export class LogLocalStorage extends LogPublisher {
  constructor() {
    // Must call super() from derived classes
    super();
    // Set location
    this.location = 'logging';
  }

  // Append log entry to local storage
  log(entry: LogEntry): Observable<boolean> {
    let ret = false;
    let values: LogEntry[];

    try {
      // Retrieve previous values from local storage
      values = JSON.parse(localStorage.getItem(this.location)) || [];
      // Add new log entry to array
      values.push(entry);
      // Store array into local storage
      localStorage.setItem(this.location, JSON.stringify(values));

      // Set return value
      ret = true;
    } catch (ex) {
      // Display error in console
      console.log(ex);
    }

    return of(ret);
  }

  // Clear all log entries from local storage
  clear(): Observable<boolean> {
    localStorage.removeItem(this.location);
    return of(true);
  }
}

// ****************************************************
// Logging Web API Class
// ****************************************************
export class LogWebApi extends LogPublisher {
  correlationId: string;

  constructor(private http: HttpClient) {
    // Must call super() from derived classes
    super();
    // Create correlationId for the entire app
    this.correlationId = uuidv4().split('-').slice(0, 2).join('-');
    // Set location
    this.location = environment.loggingUrl;
  }

  handleErrors(error: any): Observable<any> {
    const errors: string[] = [];
    let msg = '';

    msg = 'Status: ' + error.status;
    msg += ' - Status Text: ' + error.statusText;
    if (error && error.json && typeof error.json === 'function') {
      msg += ' - Exception Message: ' + error.json().exceptionMessage;
    } else {
      msg += ' - Exception Message' + JSON.stringify(error.stack);
    }
    errors.push(msg);

    console.error('An error occurred', errors);
    return throwError(errors);
  }

  // Add log entry to back end data store
  log(entry: LogEntry): Promise<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const options = {
      headers
    };

    return this.http.post(this.location,
      {
        level: entry.level,
        source: 'UI',
        message: entry.message,
        extraInfo: entry.extraInfo,
        correlationId: this.correlationId
      }, options)
      .toPromise()
      .then((response: ServerResponse) => response.data)
      .catch(this.handleErrors);
  }

  // Clear all log entries from local storage
  clear(): Observable<boolean> {
    return of(true);
  }
}
